import { useEffect, useState, useRef, useMemo, useCallback } from "react";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { toast } from "sonner";
import { toastError } from "@/lib/toastError";
import { fmtNum } from "@/lib/format";

// All global-markets page state + handlers live here so the page can be composed
// from small presentational components without prop-drilling dozens of values.
export function useBroker() {
  const { refreshUser } = useApp();
  const [status, setStatus] = useState(null);
  const [positions, setPositions] = useState([]);
  const [orders, setOrders] = useState([]);
  const [fills, setFills] = useState([]);
  const [markets, setMarkets] = useState(null);
  const [loading, setLoading] = useState(true);
  const [watchSet, setWatchSet] = useState(new Set());
  const [exSel, setExSel] = useState(null);
  const [exListings, setExListings] = useState([]);
  const [exLoading, setExLoading] = useState(false);

  // order ticket
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState(null);
  const [side, setSide] = useState("buy");
  const [qty, setQty] = useState(1);
  const [sizeMode, setSizeMode] = useState("shares"); // shares | amount (DriveWealth-style notional)
  const [amountCash, setAmountCash] = useState(25);
  const [orderType, setOrderType] = useState("market");
  const [limitPrice, setLimitPrice] = useState("");
  const [takeProfit, setTakeProfit] = useState("");
  const [stopLoss, setStopLoss] = useState("");
  const [trailPercent, setTrailPercent] = useState("");
  const [autoFx, setAutoFx] = useState(true);
  const [nowTick, setNowTick] = useState(Date.now());
  const [busy, setBusy] = useState(false);
  const [connectOpen, setConnectOpen] = useState(false);
  const [connForm, setConnForm] = useState({ base_url: "", account_id: "" });
  const [connecting, setConnecting] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const debounceRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const [s, p, o, f, m, w] = await Promise.all([
        api.get("/broker/status"), api.get("/broker/positions"),
        api.get("/broker/orders"), api.get("/broker/fills"), api.get("/broker/markets"),
        api.get("/watchlist"),
      ]);
      setStatus(s.data); setPositions(p.data.positions || []);
      setOrders(o.data.orders || []); setFills(f.data.fills || []); setMarkets(m.data);
      setWatchSet(new Set((w.data || []).map((x) => x.symbol)));
    } catch (e) { toastError("Failed to load broker data", e, { id: "broker-load" }); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openExchange = async (ex) => {
    setExSel(ex); setExListings([]); setExLoading(true);
    try {
      const { data } = await api.get("/market/instruments", { params: { market: ex.code } });
      setExListings((data || []).slice(0, 60));
    } catch (e) { toastError("Failed to load listings", e); }
    finally { setExLoading(false); }
  };
  const toggleWatch = async (sym) => {
    const has = watchSet.has(sym);
    try {
      if (has) await api.delete(`/watchlist/${encodeURIComponent(sym)}`);
      else await api.post("/watchlist", { symbol: sym });
      setWatchSet((s) => { const n = new Set(s); if (has) n.delete(sym); else n.add(sym); return n; });
      toast.success(has ? "Removed from watchlist" : "Added to watchlist", { description: sym });
    } catch (e) { toastError("Failed to update watchlist", e); }
  };

  // One-tap price alert (±5% recurring move) straight from the exchange listings.
  const quickAlert = async (sym) => {
    try {
      await api.post("/alerts", { symbol: sym, alert_type: "percent_move", percent: 5, recurring: true });
      toast.success("Alert created", { description: `${sym.split(".")[0]} · notify on ±5% move` });
    } catch (e) {
      toastError("Could not create alert", e);
    }
  };

  // Ticking clock so queued-order countdowns update live.
  useEffect(() => {
    const iv = setInterval(() => setNowTick(Date.now()), 1000);
    return () => clearInterval(iv);
  }, []);
  const queuedOrders = orders.filter((o) => o.queued && o.status === "working");

  // Lookup: exchange code -> market meta (for order-ticket badges).
  const marketByCode = useMemo(() => {
    const map = {};
    (markets?.regions || []).forEach((r) => r.exchanges.forEach((ex) => { map[ex.code] = ex; }));
    return map;
  }, [markets]);
  const selMarket = selected ? marketByCode[selected.market] : null;

  const search = (q) => {
    setQuery(q);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!q || q.length < 2) { setResults([]); return; }
    debounceRef.current = setTimeout(async () => {
      try {
        const { data } = await api.get("/market/instruments", { params: { search: q } });
        setResults((data || []).slice(0, 8));
      } catch (e) { setResults([]); }
    }, 250);
  };
  const pick = (inst) => { setSelected(inst); setResults([]); setQuery(`${inst.symbol} — ${inst.name}`); setLimitPrice(fmtNum(inst.price)); };
  const clearSel = () => { setSelected(null); setQuery(""); setResults([]); setLimitPrice(""); setTakeProfit(""); setStopLoss(""); setTrailPercent(""); };

  const notionalMode = () => sizeMode === "amount" && side === "buy";
  const buildOrderBody = () => {
    const body = { symbol: selected.symbol, side, order_type: notionalMode() ? "market" : orderType };
    if (notionalMode()) body.amount_cash = Number(amountCash);
    else body.qty = Number(qty);
    if (!notionalMode() && orderType === "limit") body.limit_price = Number(limitPrice);
    if (side === "buy") {
      if (takeProfit) body.take_profit = Number(takeProfit);
      if (stopLoss) body.stop_loss = Number(stopLoss);
      if (trailPercent) body.stop_trail_percent = Number(trailPercent);
      if (selected.currency && selected.currency !== "USD") body.auto_fx = autoFx;
    }
    return body;
  };

  const placeOrder = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      const body = buildOrderBody();
      const { data } = await api.post("/broker/orders", body);
      const st = data.order.status;
      const nb = (data.brackets || []).length;
      const afx = data.order.auto_fx_applied;
      const sizeLabel = notionalMode() ? `$${amountCash}` : `${data.order.qty} sh`;
      if (data.queued) {
        toast.warning("Order queued — market closed", {
          description: data.status_note || `${selMarket?.name || selected.market} is closed. Fills at next open.`,
        });
      } else {
        toast.success(st === "filled" ? "Order filled" : "Order working", {
          description: `${side.toUpperCase()} ${sizeLabel} ${selected.symbol}${st === "filled" ? ` @ ${fmtNum(data.order.fill_price)}` : ""}${nb ? ` · ${nb} bracket order(s)` : ""}${afx ? ` · auto-converted $${afx.converted_usd} → ${afx.ccy}` : ""}`,
        });
      }
      setReviewOpen(false);
      clearSel(); setQty(1); setOrderType("market"); setSizeMode("shares"); setAmountCash(25);
      await Promise.all([load(), refreshUser()]);
    } catch (e) { toastError("Order rejected", e); }
    finally { setBusy(false); }
  };

  const openReview = async () => {
    setPreview(null);
    setReviewOpen(true);
    try {
      const { data } = await api.post("/broker/preview", buildOrderBody());
      setPreview(data);
    } catch (e) { console.error(e); /* dialog still shows base details */ }
  };

  const cancel = async (id) => {
    try { await api.post(`/broker/orders/${id}/cancel`); toast.success("Order cancelled"); await load(); }
    catch (e) { toastError("Cancel failed", e); }
  };

  const sync = async () => {
    try {
      const { data } = await api.post("/broker/sync");
      toast.success(data.filled > 0 ? `${data.filled} order(s) filled` : "No marketable orders");
      await Promise.all([load(), refreshUser()]);
    } catch (e) { toastError("Sync failed", e); }
  };

  const connect = async () => {
    if (!connForm.base_url || !connForm.account_id) return;
    setConnecting(true);
    try {
      const { data } = await api.post("/broker/connect", connForm);
      if (data.connected) toast.success("Gateway connected", { description: data.message });
      else toast.warning("Saved — gateway unreachable", { description: data.message });
      setConnectOpen(false);
      await load();
    } catch (e) { toastError("Connect failed", e); }
    finally { setConnecting(false); }
  };

  const disconnect = async () => {
    try { await api.post("/broker/disconnect"); toast.success("Disconnected — back to simulated"); await load(); }
    catch (e) { toastError("Failed", e); }
  };

  const estCostUsd = selected ? (Number(qty) || 0) * selected.price : 0;
  const openOrders = orders.filter((o) => o.status === "working");

  return {
    // data
    status, positions, orders, fills, markets, loading, watchSet,
    exSel, setExSel, exListings, exLoading,
    // ticket state
    query, results, selected, side, setSide, qty, setQty, sizeMode, setSizeMode,
    amountCash, setAmountCash, orderType, setOrderType, limitPrice, setLimitPrice,
    takeProfit, setTakeProfit, stopLoss, setStopLoss, trailPercent, setTrailPercent,
    autoFx, setAutoFx, nowTick, busy,
    // connect/review
    connectOpen, setConnectOpen, connForm, setConnForm, connecting,
    reviewOpen, setReviewOpen, preview,
    // computed
    queuedOrders, marketByCode, selMarket, notionalMode, estCostUsd, openOrders,
    // handlers
    load, openExchange, toggleWatch, quickAlert, search, pick, clearSel,
    placeOrder, openReview, cancel, sync, connect, disconnect,
  };
}
